import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, History, ArrowDownRight, ArrowUpRight, Clock } from "lucide-react";

function fmt(ts) {
  try {
    return new Date(ts).toLocaleString("he-IL");
  } catch {
    return ts;
  }
}

export default function HistoryPanel({ open, onClose, history }) {
  const [idx, setIdx] = useState(0);

  const diff = useMemo(() => {
    if (!history || history.length === 0) return { appeared: [], disappeared: [] };
    const cur = history[idx];
    const prev = history[idx + 1];
    const curIps = new Set((cur?.devices || []).map((d) => d.ip));
    const prevIps = new Set((prev?.devices || []).map((d) => d.ip));
    const appeared = (cur?.devices || []).filter((d) => !prevIps.has(d.ip));
    const disappeared = (prev?.devices || []).filter((d) => !curIps.has(d.ip));
    return { appeared, disappeared, hasPrev: !!prev };
  }, [history, idx]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 z-30 bg-black/20 backdrop-blur-[2px]"
            data-testid="history-overlay"
          />
          <motion.div
            initial={{ x: -420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -420, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            dir="rtl"
            className="absolute bottom-4 left-4 top-4 z-40 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col rounded-2xl border border-white/60 bg-white/85 shadow-2xl backdrop-blur-xl"
            data-testid="history-panel"
          >
            <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
              <div className="flex items-center gap-2">
                <History size={17} className="text-[#0055FF]" />
                <h2 className="text-[14px] font-bold text-[#0A0A0A]">היסטוריית סריקות</h2>
              </div>
              <button
                data-testid="history-close"
                onClick={onClose}
                className="rounded-lg p-1 text-[#52525B] transition-colors hover:bg-black/5"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[38%] overflow-y-auto border-b border-black/5 p-3">
              {(!history || history.length === 0) && (
                <p className="py-8 text-center text-[13px] text-[#94A3B8]">
                  אין עדיין היסטוריית סריקות.
                </p>
              )}
              <div className="space-y-1.5">
                {(history || []).map((h, i) => (
                  <button
                    key={h.id || i}
                    data-testid={`history-item-${i}`}
                    onClick={() => setIdx(i)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-right transition-colors ${
                      i === idx ? "bg-[#0055FF]/10" : "hover:bg-black/5"
                    }`}
                  >
                    <span className="flex items-center gap-2 text-[11px] text-[#52525B]">
                      <Clock size={12} />
                      {fmt(h.scanned_at)}
                    </span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-[#0A0A0A] ring-1 ring-black/5">
                      {h.device_count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="mb-3 text-[12px] font-bold text-[#0A0A0A]">
                שינויים מהסריקה הקודמת
              </h3>
              {!diff.hasPrev && (
                <p className="text-[12px] text-[#94A3B8]">
                  זו הסריקה הראשונה — אין עם מה להשוות.
                </p>
              )}

              {diff.appeared?.length > 0 && (
                <div className="mb-4">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
                    <ArrowUpRight size={13} /> התחברו ({diff.appeared.length})
                  </p>
                  <div className="space-y-1">
                    {diff.appeared.map((d) => (
                      <div
                        key={d.ip}
                        dir="ltr"
                        className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-2.5 py-1.5"
                      >
                        <span className="font-mono text-[12px] font-semibold text-[#0A0A0A]">
                          {d.ip}
                        </span>
                        {d.hostname && (
                          <span className="ms-2 text-[10px] text-[#52525B]">
                            {d.hostname}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diff.disappeared?.length > 0 && (
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-rose-500">
                    <ArrowDownRight size={13} /> התנתקו ({diff.disappeared.length})
                  </p>
                  <div className="space-y-1">
                    {diff.disappeared.map((d) => (
                      <div
                        key={d.ip}
                        dir="ltr"
                        className="rounded-lg border border-rose-200 bg-rose-50/70 px-2.5 py-1.5"
                      >
                        <span className="font-mono text-[12px] font-semibold text-[#0A0A0A]">
                          {d.ip}
                        </span>
                        {d.hostname && (
                          <span className="ms-2 text-[10px] text-[#52525B]">
                            {d.hostname}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diff.hasPrev &&
                diff.appeared?.length === 0 &&
                diff.disappeared?.length === 0 && (
                  <p className="text-[12px] text-[#94A3B8]">אין שינויים — הרשת יציבה.</p>
                )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
